import React, { useCallback, useRef, useState } from 'react'
import type { Appointment, AppointmentV2 } from '@dialogue/timekeeper'
import { AppointmentBookingBase } from 'APP/Components/AppointmentBooking/AppointmentBookingBase'
import BookingConfirmation, {
  type BookingConfirmationProps,
} from 'APP/Components/AppointmentBooking/common/BookingConfirmation'
import { getBookingWindowText } from 'APP/Components/AppointmentBooking/common/bookingWindowText'
import type { Answer } from 'APP/Components/AppointmentBooking/utils/types'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { logger } from 'APP/Lib/Datadog'
import type { Practitioners } from 'APP/Lib/EpisodeListHelpers'
import { getPractitionerRoleLabel } from 'APP/Lib/PractitionerHelpers'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import { selectPatientProfile } from 'APP/Redux/PatientRedux'
import type { MemberTaskViewProps } from '../types'

type ConfirmationDetails = Omit<BookingConfirmationProps, 'onClose'>

const MemberTaskViewBooking = ({
  memberTask,
  onSuccess,
  onError,
  onClose,
}: MemberTaskViewProps) => {
  const bookingId = memberTask.parameters.booking_id
  const episodeId = memberTask.episode_id
  const hasBooked = useRef(false)
  const dispatch = useDispatch()
  const [confirmation, setConfirmation] = useState<ConfirmationDetails | null>(null)

  const profile = useSelector(selectPatientProfile)
  const issueTypeId = useSelector((state) => state.history.episodes[episodeId]?.healthIssueTypeId)
  const practitioners = useSelector((state) => state.history.practitioners) as Practitioners
  const providers = useSelector((state) => state.providerBooking.providers.data)

  const onComplete = useCallback(
    (answer: Answer, appointment?: Appointment | AppointmentV2) => {
      if (!answer?.payload?.is_booked) return
      hasBooked.current = true
      dispatch(PatientHistoryActions.refreshAppointmentsRequest())
      onSuccess()

      if (!appointment) {
        logger.error('[MemberTaskViewBooking] booked without appointment, skipping confirmation', {
          bookingId,
          episodeId,
        })
        return onClose()
      }

      const { provider_id, start_at, end_at } = appointment
      const practitioner = Object.values(practitioners ?? {}).find(
        (p) => p.app_id === String(provider_id)
      )
      const provider = provider_id ? providers[provider_id] : undefined

      setConfirmation({
        memberName: profile?.preferredName || profile?.givenName || '',
        providerName: [practitioner?.first_name, practitioner?.last_name].filter(Boolean).join(' '),
        providerRole: getPractitionerRoleLabel(provider?.licenses, provider?.jobTitle),
        providerPicture: practitioner?.avatar_url,
        startAt: start_at ?? '',
        endAt: end_at ?? '',
        episodeId,
        issueTypeId,
        lateChangeFee: appointment.cancellation_fee,
      })
    },
    [
      dispatch,
      onSuccess,
      onClose,
      practitioners,
      providers,
      profile,
      bookingId,
      episodeId,
      issueTypeId,
    ]
  )

  const handleOnClose = useCallback(() => {
    if (hasBooked.current) return
    onClose()
  }, [onClose])

  if (confirmation) {
    return <BookingConfirmation {...confirmation} onClose={onClose} />
  }

  return (
    <AppointmentBookingBase
      derive_from_booking
      hide_past_days
      booking_id={bookingId}
      is_rescheduling={false}
      close={handleOnClose}
      onComplete={onComplete}
      onError={onError}
      customHeaderText={getBookingWindowText}
    />
  )
}

export default MemberTaskViewBooking
